import { useEffect, useRef } from 'react';
import { api, imageUrl } from '../services/api';
import { isNativePhotoVault, warmProductImages } from '../utils/imageCache';

const PREFETCH_KEY = 'oh_cats_prefetch_v2';
const PREFETCH_TTL_MS = 15 * 60 * 1000;
const PER_CAT = 100;
const MAX_CATS = 16;

/**
 * Precarga productos + fotos de las categorías principales
 * para que al entrar a cada una se vea casi al instante.
 */
export default function usePrefetchCatalogImages({ enabled = true } = {}) {
  const started = useRef(false);

  useEffect(() => {
    if (!enabled || !isNativePhotoVault() || started.current) return undefined;
    started.current = true;
    let cancelled = false;

    (async () => {
      try {
        const raw = sessionStorage.getItem(PREFETCH_KEY);
        if (raw) {
          const prev = JSON.parse(raw);
          if (prev?.at && Date.now() - prev.at < PREFETCH_TTL_MS) return;
        }
      } catch {
        /* ignore */
      }

      try {
        const catsRes = await api.getCategorias();
        if (cancelled) return;
        const cats = (catsRes.data || [])
          .map((c) => c.nombre || c)
          .filter((n) => n && n !== 'Todas')
          .slice(0, MAX_CATS);

        // Primero "Todas" (más usado), luego el resto.
        const queue = ['', ...cats];
        const allUrls = [];

        for (const cat of queue) {
          if (cancelled) return;
          try {
            const res = await api.getProductos({
              q: '',
              subcategoria: cat || '',
              offset: 0,
              limit: PER_CAT,
            });
            const rows = res.data || [];
            for (const p of rows) {
              const u = imageUrl(p.imagen);
              if (u) allUrls.push(u);
            }
            // Pausa corta para no saturar LVE, pero sin que se sienta lento.
            await new Promise((r) => setTimeout(r, 60));
          } catch {
            /* seguir con la siguiente */
          }
        }

        if (!cancelled) {
          warmProductImages(allUrls, { limit: 500 });
          try {
            sessionStorage.setItem(PREFETCH_KEY, JSON.stringify({ at: Date.now(), n: allUrls.length }));
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
